import React, {useState} from "react";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {
    backgroundColor,
    borderRadius,
    calculateRemSize,
    display,
    flex,
    flexAbsoluteCenter,
    flexDirection,
    mb,
    minWidth,
    mr,
    mt,
    onDesktop,
    p,
    pointer,
    position,
    pv,
    w33,
    w50,
    w66,
    w100
} from "@pg-design/helpers-css";
import {ChevronUpIcon} from "@pg-design/icons-module";
import {Image, Placeholder} from "@pg-design/image-module";
import {Text} from "@pg-design/text-module";
import {CREDIT_PAGES_IDS} from "@pg-mono/e2e-test-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {CreditApplicationSource} from "../../../application/utils/CreditApplicationSource";
import {creditCalculatorAnalytics, CreditCalculatorGTMEvent, CreditCalculatorListingActions} from "../../tracking/credit_gtm";
import {ICreditData} from "../../types/ICreditData";
import {ICreditOffer} from "../../types/ICreditOffer";
import {getBankLogo} from "../../utils/get_bank_logo";
import {getSecondaryDataFromOffer} from "../../utils/get_secondary_data_from_offer";
import {CreditOfferBoxMainData, IMainDataProps} from "./CreditOfferBoxMainData";

interface IProps {
    offer: ICreditOffer;
    onButtonClick: (payload: ICreditData) => void;
}

export const CreditOfferBox = (props: IProps) => {
    const {offer, onButtonClick} = props;

    const [showDetails, setShowDetails] = useState(false);
    const theme = useTheme();
    const {isMobile} = useUserDevice();

    const logo = getBankLogo(offer.bank);
    const offerName = offer.offer_name.split(" - ")[0];
    const mainDataProps: IMainDataProps = {
        fixedInterestPercent: offer.fixed_interest_rate_percent,
        commission: offer.commission,
        equalInstallmentRate: offer.equal_installment_rate,
        rrso: offer.rrso
    };

    const moreData = getSecondaryDataFromOffer(offer);

    const onClick = (source: CreditApplicationSource) => {
        const payload: ICreditData = {
            source: source,
            term_of_the_loan: offer.max_loan_period,
            bank: offer.bank,
            mortgage_offer_name: offerName,
            credit_amount: Math.ceil(offer.max_loan_price)
        };

        onButtonClick(payload);
    };

    const onShowDetails = () => {
        creditCalculatorAnalytics.gtm.creditCalculatorEvent(CreditCalculatorGTMEvent.CREDIT_INSTALMENT_CALCULATOR, {
            credit_action: showDetails ? CreditCalculatorListingActions.HIDE_DETAILS : CreditCalculatorListingActions.SHOW_DETAILS
        });
        setShowDetails(!showDetails);
    };

    const renderButtons = () => (
        <div css={buttonsHolder}>
            <Button
                variant="filled_primary"
                size="x-small"
                onClick={() => onClick(CreditApplicationSource.MORTGAGE_APPLICATION_SOURCE_CREDIT)}
                css={[btnStyle, btnMargin]}
            >
                <Text variant="button_small" dataTestId={CREDIT_PAGES_IDS.COMPARE_CREDITS_SUBPAGE.CREDIT_OFFER_REQUEST_OPEN_BUTTON}>
                    Zapytaj o kredyt
                </Text>
            </Button>
            <Button
                variant="outlined_secondary"
                size="x-small"
                onClick={() => onClick(CreditApplicationSource.MORTGAGE_APPLICATION_SOURCE_CREDIT_RATING)}
                css={btnStyle}
            >
                <Text variant="button_small" dataTestId={CREDIT_PAGES_IDS.COMPARE_CREDITS_SUBPAGE.CREDITWORTHINESS_FORM_OPEN_BUTTON}>
                    Sprawdź zdolność kredytową
                </Text>
            </Button>
        </div>
    );

    return (
        <div css={boxWrapper}>
            <div css={boxHeaderWrapper}>
                {logo ? (
                    <Image src={logo} alt="Kalkulator" width="96" height="96" css={[minWidth("96px")]} />
                ) : (
                    <div css={placeholderWrapper}>
                        <Placeholder />
                    </div>
                )}
                <div css={boxHeaderTexts}>
                    <Text variant="headline_3" color={theme.colors.gray[600]} dataTestId={CREDIT_PAGES_IDS.COMPARE_CREDITS_SUBPAGE.BANK_NAME_BOX}>
                        {offer.bank}
                    </Text>
                    <Text variant="headline_3" dataTestId={CREDIT_PAGES_IDS.COMPARE_CREDITS_SUBPAGE.CREDIT_TITLE_BOX}>
                        {offerName}
                    </Text>
                </div>
            </div>

            <CreditOfferBoxMainData {...mainDataProps} />

            <div css={buttonsWrapper}>
                {!isMobile && renderButtons()}

                <div css={[flexAbsoluteCenter, pointer]} onClick={onShowDetails}>
                    <Text as="div" variant="button_medium" css={mr(1)}>
                        szczegóły oferty
                    </Text>
                    <DetailsArrowIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" showDetails={showDetails} />
                </div>
            </div>

            <SecondaryDataWrapper showDetails={showDetails}>
                {moreData.map((data) => {
                    if (!data.value) {
                        return null;
                    }
                    return (
                        <div key={data.title} css={secondaryDataHolder}>
                            <Text variant="body_copy_2" css={onDesktop(w33)}>
                                {data.title}
                            </Text>
                            <Text variant="info_txt_1" color={theme.colors.gray[800]} css={onDesktop(w66)}>
                                <span dangerouslySetInnerHTML={{__html: data.value}} />
                            </Text>
                        </div>
                    );
                })}
            </SecondaryDataWrapper>

            {isMobile && renderButtons()}
        </div>
    );
};

const placeholderWrapper = css`
    ${position("relative")};
    width: 96px;
    height: 96px;
`;

const boxWrapper = css`
    ${backgroundColor("#FFF")};
    ${elevation(2)};
    ${w100};
    ${p(2)};
    ${borderRadius(2)};

    ${onDesktop(p(3))};
`;

const boxHeaderWrapper = css`
    ${flex("center", "start")};
    ${flexDirection("row")};
    gap: ${calculateRemSize(1)};
`;

const boxHeaderTexts = css`
    ${flex()};
    ${flexDirection("column")};
`;

const buttonsWrapper = css`
    ${flex("center", "space-between")};
    ${flexDirection("column-reverse")};
    ${mb(3)};

    ${onDesktop(flexDirection("row"), mb(0))};
`;

const buttonsHolder = css`
    ${display("inline-flex")};
    ${flexDirection("column")};
    ${w100};

    ${onDesktop(flexDirection("row"), w50)};
`;

const btnStyle = css`
    width: 100%;

    ${onDesktop(css`
        width: unset;
    `)};
`;

const btnMargin = css`
    ${mb(2)};

    ${onDesktop(mr(2), mb(0))};
`;

const DetailsArrowIcon = styled(ChevronUpIcon)<{showDetails: boolean}>`
    transform: rotate(${(props) => (props.showDetails ? 0 : "180deg")});
    transition: transform 0.4s linear;
`;

const SecondaryDataWrapper = styled.div<{showDetails: boolean}>`
    max-height: ${(props) => (props.showDetails ? "500px" : "0")};
    transition: max-height 0.4s ease-out;
    overflow: hidden;

    & > div:first-of-type {
        ${onDesktop(mt(4))};
    }

    & div:last-child {
        border-bottom: unset;
        ${mb(4)};
        ${onDesktop(mb(0))};
    }
`;
const secondaryDataHolder = (theme: Theme) => css`
    ${flex("start", "space-between")};
    ${flexDirection("column")};
    ${w100};
    border-bottom: 1px solid ${theme.colors.gray[500]};
    ${pv(1)};
    gap: ${calculateRemSize(0.5)};

    ${onDesktop(flex("center", "start"), flexDirection("row"))};
`;
import React, {useState} from "react";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {
    backgroundColor,
    borderRadius,
    calculateRemSize,
    display,
    flex,
    flexAbsoluteCenter,
    flexDirection,
    mb,
    minWidth,
    mr,
    mt,
    onDesktop,
    p,
    pointer,
    position,
    pv,
    w33,
    w50,
    w66,
    w100
} from "@pg-design/helpers-css";
import {ChevronUpIcon} from "@pg-design/icons-module";
import {Image, Placeholder} from "@pg-design/image-module";
import {Text} from "@pg-design/text-module";
import {CREDIT_PAGES_IDS} from "@pg-mono/e2e-test-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {CreditApplicationSource} from "../../../application/utils/CreditApplicationSource";
import {creditCalculatorAnalytics, CreditCalculatorGTMEvent, CreditCalculatorListingActions} from "../../tracking/credit_gtm";
import {ICreditData} from "../../types/ICreditData";
import {ICreditOffer} from "../../types/ICreditOffer";
import {getBankLogo} from "../../utils/get_bank_logo";
import {getSecondaryDataFromOffer} from "../../utils/get_secondary_data_from_offer";
import {CreditOfferBoxMainData, IMainDataProps} from "./CreditOfferBoxMainData";

interface IProps {
    offer: ICreditOffer;
    onButtonClick: (payload: ICreditData) => void;
}

export const CreditOfferBox = (props: IProps) => {
    const {offer, onButtonClick} = props;

    const [showDetails, setShowDetails] = useState(false);
    const theme = useTheme();
    const {isMobile} = useUserDevice();

    const logo = getBankLogo(offer.bank);
    const offerName = offer.offer_name.split(" - ")[0];
    const mainDataProps: IMainDataProps = {
        fixedInterestPercent: offer.fixed_interest_rate_percent,
        commission: offer.commission,
        equalInstallmentRate: offer.equal_installment_rate,
        rrso: offer.rrso
    };

    const moreData = getSecondaryDataFromOffer(offer);

    const onClick = (source: CreditApplicationSource) => {
        const payload: ICreditData = {
            source: source,
            term_of_the_loan: offer.max_loan_period,
            bank: offer.bank,
            mortgage_offer_name: offerName,
            credit_amount: Math.ceil(offer.max_loan_price)
        };

        onButtonClick(payload);
    };

    const onShowDetails = () => {
        creditCalculatorAnalytics.gtm.creditCalculatorEvent(CreditCalculatorGTMEvent.CREDIT_INSTALMENT_CALCULATOR, {
            credit_action: showDetails ? CreditCalculatorListingActions.HIDE_DETAILS : CreditCalculatorListingActions.SHOW_DETAILS
        });
        setShowDetails(!showDetails);
    };

    const renderButtons = () => (
        <div css={buttonsHolder}>
            <Button
                variant="filled_primary"
                size="x-small"
                onClick={() => onClick(CreditApplicationSource.MORTGAGE_APPLICATION_SOURCE_CREDIT)}
                css={[btnStyle, btnMargin]}
            >
                <Text variant="button_small" dataTestId={CREDIT_PAGES_IDS.COMPARE_CREDITS_SUBPAGE.CREDIT_OFFER_REQUEST_OPEN_BUTTON}>
                    Zapytaj o kredyt
                </Text>
            </Button>
            <Button
                variant="outlined_secondary"
                size="x-small"
                onClick={() => onClick(CreditApplicationSource.MORTGAGE_APPLICATION_SOURCE_CREDIT_RATING)}
                css={btnStyle}
            >
                <Text variant="button_small" dataTestId={CREDIT_PAGES_IDS.COMPARE_CREDITS_SUBPAGE.CREDITWORTHINESS_FORM_OPEN_BUTTON}>
                    Sprawdź zdolność kredytową
                </Text>
            </Button>
        </div>
    );

    return (
        <div css={boxWrapper}>
            <div css={boxHeaderWrapper}>
                {logo ? (
                    <Image src={logo} alt="Kalkulator" width="96" height="96" css={[minWidth("96px")]} />
                ) : (
                    <div css={placeholderWrapper}>
                        <Placeholder />
                    </div>
                )}
                <div css={boxHeaderTexts}>
                    <Text variant="headline_3" color={theme.colors.gray[600]} dataTestId={CREDIT_PAGES_IDS.COMPARE_CREDITS_SUBPAGE.BANK_NAME_BOX}>
                        {offer.bank}
                    </Text>
                    <Text variant="headline_3" dataTestId={CREDIT_PAGES_IDS.COMPARE_CREDITS_SUBPAGE.CREDIT_TITLE_BOX}>
                        {offerName}
                    </Text>
                </div>
            </div>

            <CreditOfferBoxMainData {...mainDataProps} />

            <div css={buttonsWrapper}>
                {!isMobile && renderButtons()}

                <div css={[flexAbsoluteCenter, pointer]} onClick={onShowDetails}>
                    <Text as="div" variant="button_medium" css={mr(1)}>
                        szczegóły oferty
                    </Text>
                    <DetailsArrowIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" showDetails={showDetails} />
                </div>
            </div>

            <SecondaryDataWrapper showDetails={showDetails}>
                {moreData.map((data) => {
                    if (!data.value) {
                        return null;
                    }
                    return (
                        <div key={data.title} css={secondaryDataHolder}>
                            <Text variant="body_copy_2" css={onDesktop(w33)}>
                                {data.title}
                            </Text>
                            <Text variant="info_txt_1" color={theme.colors.gray[800]} css={onDesktop(w66)}>
                                <span dangerouslySetInnerHTML={{__html: data.value}} />
                            </Text>
                        </div>
                    );
                })}
            </SecondaryDataWrapper>

            {isMobile && renderButtons()}
        </div>
    );
};

const placeholderWrapper = css`
    ${position("relative")};
    width: 96px;
    height: 96px;
`;

const boxWrapper = css`
    ${backgroundColor("#FFF")};
    ${elevation(2)};
    ${w100};
    ${p(2)};
    ${borderRadius(2)};

    ${onDesktop(p(3))};
`;

const boxHeaderWrapper = css`
    ${flex("center", "start")};
    ${flexDirection("row")};
    gap: ${calculateRemSize(1)};
`;

const boxHeaderTexts = css`
    ${flex()};
    ${flexDirection("column")};
`;

const buttonsWrapper = css`
    ${flex("center", "space-between")};
    ${flexDirection("column-reverse")};
    ${mb(3)};

    ${onDesktop(flexDirection("row"), mb(0))};
`;

const buttonsHolder = css`
    ${display("inline-flex")};
    ${flexDirection("column")};
    ${w100};

    ${onDesktop(flexDirection("row"), w50)};
`;

const btnStyle = css`
    width: 100%;

    ${onDesktop(css`
        width: unset;
    `)};
`;

const btnMargin = css`
    ${mb(2)};

    ${onDesktop(mr(2), mb(0))};
`;

const DetailsArrowIcon = styled(ChevronUpIcon)<{showDetails: boolean}>`
    transform: rotate(${(props) => (props.showDetails ? 0 : "180deg")});
    transition: transform 0.4s linear;
`;

const SecondaryDataWrapper = styled.div<{showDetails: boolean}>`
    max-height: ${(props) => (props.showDetails ? "500px" : "0")};
    transition: max-height 0.4s ease-out;
    overflow: hidden;

    & > div:first-of-type {
        ${onDesktop(mt(4))};
    }

    & div:last-child {
        border-bottom: unset;
        ${mb(4)};
        ${onDesktop(mb(0))};
    }
`;
const secondaryDataHolder = (theme: Theme) => css`
    ${flex("start", "space-between")};
    ${flexDirection("column")};
    ${w100};
    border-bottom: 1px solid ${theme.colors.gray[500]};
    ${pv(1)};
    gap: ${calculateRemSize(0.5)};

    ${onDesktop(flex("center", "start"), flexDirection("row"))};
`;
